import { useEffect, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { topUpLink } from '../lib/billingFlow'
import { api } from '../lib/api'
import { planPrice, money, calendarDate } from '../lib/format'
import { useApi } from '../lib/useApi'
import type { Project, PlanQuote, PlanTier } from '../lib/types'
import { Button, ConfirmDialog } from './ui'

export default function PaidDomainPlan({ project, reload }: { project: Project; reload: () => void }) {
  const location = useLocation()
  const [params] = useSearchParams()
  const billing = project.billing!
  const { data: plans } = useApi(() => api.plans(), [])
  const [tier, setTier] = useState<PlanTier>((['starter', 'standard', 'pro', 'max', 'enterprise'].includes(params.get('paidTier') ?? '') ? params.get('paidTier') : billing.plan === 'free' ? 'starter' : billing.plan) as PlanTier)
  const [quote, setQuote] = useState<PlanQuote | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [cancel, setCancel] = useState(false)
  const [version, setVersion] = useState(0)
  const eligible = billing.custom_domains_allowed === true
  const subscribed = billing.paid_features_enabled === true
  const owner = project.role === 'owner'
  useEffect(() => {
    let active = true
    if (owner && project.services.length > 0 && (!eligible || !subscribed)) {
      api.paidPlanQuote(project.id, tier).then(result => { if (active) setQuote(result) })
        .catch(e => { if (active) setError(e.message) })
    }
    return () => { active = false }
  }, [project.id, project.services.length, tier, owner, eligible, subscribed, version])

  async function purchase() {
    if (!quote || quote.new_plan !== tier || !quote.affordable) return
    setBusy(true); setError('')
    try { await api.buyPaidPlan(project.id, tier, String(quote.amount_due)); reload() }
    catch (e) { setError(e instanceof Error ? e.message : 'Upgrade failed.'); setQuote(null); setVersion(v => v + 1) }
    finally { setBusy(false) }
  }
  async function stop() {
    setBusy(true); setError('')
    try { await api.cancelPaidPlan(project.id); setCancel(false); reload() }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not turn off paid features.') }
    finally { setBusy(false) }
  }
  return <div className="card stack" aria-labelledby="paid-domains-title">
    <h3 id="paid-domains-title">Custom domains &amp; paid plan</h3>
    <p className="muted">Free websites and trials use their included TalariCloud address. Connecting your own domain requires a paid Starter plan or higher, even for a static website. One application payment covers its frontend and backend.</p>
    {eligible && <p className="alert info">Custom domains available{billing.paid_until ? ` · paid until ${calendarDate(billing.paid_until)}` : ''}. <Link to={project.services[0] ? `/services/${project.services[0].id}/domains` : '/services'}>Manage domains</Link></p>}
    {!owner && <p className="tiny muted">The application owner can upgrade and manage payments.</p>}
    {!project.services.length && <p className="muted">Add an application component before choosing a paid plan.</p>}
    {owner && project.services.length > 0 && (!eligible || !subscribed) && <>
      <label className="tiny muted">Choose paid plan<select value={tier} disabled={busy} onChange={e => { setQuote(null); setTier(e.target.value as PlanTier) }}>
        {(plans ?? []).filter(p => p.custom_domains).map(p => <option key={p.tier} value={p.tier}>{p.label} · {planPrice(p)}/month</option>)}
      </select></label>
      {quote && quote.new_plan === tier ? <div className="alert info" aria-live="polite"><strong>{money(quote.amount_due, quote.currency ?? 'ETB')} from your wallet now</strong>; then {money(quote.new_monthly, quote.currency ?? 'ETB')} every 30 days while this application is running. This activates paid features immediately and ends the free compute trial for this application. It is not an extra fee per domain or component.
        {!quote.affordable && <p>Add {money(quote.shortfall ?? '0', quote.currency ?? 'ETB')} to your wallet. <Link to={topUpLink(quote.currency ?? 'ETB', quote.shortfall ?? '0', `${location.pathname}?${new URLSearchParams({ ...Object.fromEntries(params), paidTier: tier })}`)}>Add money to continue</Link></p>}
      </div> : <p className="tiny muted">Loading upgrade price…</p>}
      <div className="row"><Button variant="primary" loading={busy} disabled={!quote || quote.new_plan !== tier || !quote.affordable} onClick={purchase}>Upgrade using wallet balance</Button><Button disabled={busy} onClick={() => { setError(''); setQuote(null); setVersion(v => v + 1) }}>Refresh quote</Button></div>
    </>}
    {owner && subscribed && <Button disabled={busy} onClick={() => setCancel(true)}>Turn off paid features</Button>}
    {error && <p className="alert error" role="alert">{error}</p>}
    <ConfirmDialog open={cancel} title="Turn off paid features?" confirmLabel="Turn off paid features" onCancel={() => setCancel(false)} onConfirm={stop} busy={busy} body={<>
      Remove this application's custom domains first. Static-only websites will stop renewing and keep their free TalariCloud address. Running backend and worker components continue normal compute billing. The current paid period is not refunded.
    </>} />
  </div>
}
